import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ConcentratorsService } from '../../services/concentrators.service';
import { UserManagementService } from '../../services/user-management.service';
import { ToastService } from '../../../shared/services/toast.service';
import { AddConcentratorRequest } from '../../../shared/models/concentrator.model';
import { ServiceCenter } from '../../../shared/models/user-management.model';
import { UserRole, UserRoleIdMap } from '../../../shared/models/user.model';
import { WebMcpConfirmService } from '../webmcp-confirm.service';
import { WebMcpClient, WebMcpTool, WebMcpToolResult } from '../webmcp.types';
import {
  asTrimmedString,
  describeOptions,
  errorResult,
  extractErrorMessage,
  findMissingFields,
  matchOption,
  textResult,
} from '../webmcp-tool-helpers';

export const CREATE_DEVICE_TOOL_NAME = 'create_device';

// New devices always start in stock; it is the only status the Add Device dialog offers.
const NEW_DEVICE_STATUS = 'In Stock';

export interface CreateDeviceToolInput {
  deviceType: string;
  model: string;
  serialNumber: string;
  firmwareVersion?: string;
  serviceCenter?: string;
}

type DeviceTypeOption = { id: string; type: string; subType: string };

/**
 * Mirrors routeAccessGuard for the Devices (concentrators) page: Super Admin and Caire Admin
 * cannot manage devices, and patients/caregivers have no business creating them.
 */
export function canCreateDevices(roleId?: number): boolean {
  const blocked = [
    UserRoleIdMap[UserRole.Super_Admin],
    UserRoleIdMap[UserRole.App_Admin],
    UserRoleIdMap[UserRole.Patient],
    UserRoleIdMap[UserRole.Caregiver],
  ];
  return Number.isFinite(roleId) && !blocked.includes(Number(roleId));
}

/** Builds the "create_device" WebMCP tool, which follows the same flow as the Add Device dialog. */
@Injectable({
  providedIn: 'root',
})
export class CreateDeviceTool {
  private concentratorsService = inject(ConcentratorsService);
  private userManagementService = inject(UserManagementService);
  private confirmService = inject(WebMcpConfirmService);
  private toastService = inject(ToastService);

  /** Returns null when the signed-in user is not allowed to create devices. */
  build(currentUser: any): WebMcpTool<CreateDeviceToolInput> | null {
    const roleId = Number(currentUser?.roleId);
    if (!canCreateDevices(roleId)) {
      return null;
    }

    const isDmeAdmin = roleId === UserRoleIdMap[UserRole.DME_Admin];
    const required = ['deviceType', 'model', 'serialNumber'];

    return {
      name: CREATE_DEVICE_TOOL_NAME,
      title: 'Create device',
      description:
        'Register a new device (e.g. an oxygen concentrator) in myCAIRE inventory with status "In Stock". ' +
        'deviceType may be the device type id, type name or sub-type name. If it is unknown, the error lists the valid types. ' +
        (isDmeAdmin
          ? 'serviceCenter (name or code) is required and must belong to your DME organization. '
          : 'The device is added to your own service center. ') +
        'The signed-in person must approve the request before the device is created.',
      inputSchema: {
        type: 'object',
        properties: {
          deviceType: { type: 'string', description: 'Device type id, type name or sub-type name.' },
          model: { type: 'string', description: 'Device model.' },
          serialNumber: { type: 'string', description: 'Device serial number.' },
          firmwareVersion: { type: 'string', description: 'Firmware version (optional).' },
          ...(isDmeAdmin
            ? { serviceCenter: { type: 'string', description: 'Service center name or code within your DME organization.' } }
            : {}),
        },
        required: isDmeAdmin ? [...required, 'serviceCenter'] : required,
      },
      execute: (input, client) => this.execute(input, currentUser, isDmeAdmin, client),
    };
  }

  private async execute(
    input: CreateDeviceToolInput,
    currentUser: any,
    isDmeAdmin: boolean,
    client?: WebMcpClient,
  ): Promise<WebMcpToolResult> {
    const missing = findMissingFields(input as any, ['deviceType', 'model', 'serialNumber']);
    if (missing.length) {
      return errorResult(`Missing required fields: ${missing.join(', ')}.`);
    }

    let deviceType: DeviceTypeOption;
    let serviceCenter: ServiceCenter | undefined;

    try {
      const deviceTypes = await firstValueFrom(this.concentratorsService.getAllDeviceTypes());
      const resolved = this.resolveDeviceType(deviceTypes, asTrimmedString(input.deviceType));
      if (!resolved) {
        return errorResult(
          `Device type "${asTrimmedString(input.deviceType)}" is unknown or ambiguous. Available device types: ` +
            `${describeOptions(deviceTypes, (t) => t.id, (t) => [t.type, t.subType].filter(Boolean).join(' / '))}.`,
        );
      }
      deviceType = resolved;

      if (isDmeAdmin) {
        const dmeOrgCode = asTrimmedString(currentUser?.dmeOrgDetails?.dmeOrgCode || currentUser?.dmeOrgCode);
        const serviceCenters = dmeOrgCode
          ? await firstValueFrom(this.userManagementService.getAllServiceCenters(dmeOrgCode))
          : [];
        const requested = asTrimmedString(input.serviceCenter);
        serviceCenter = requested ? matchOption(serviceCenters, requested, (s) => s.scCode, (s) => s.name) : undefined;

        if (!serviceCenter) {
          const reason = requested ? `Service center "${requested}" was not found.` : 'serviceCenter is required.';
          return errorResult(`${reason} Available service centers: ${describeOptions(serviceCenters, (s) => s.scCode, (s) => s.name)}.`);
        }
      }
    } catch (error) {
      return errorResult(`Could not load device types or service centers: ${extractErrorMessage(error)}`);
    }

    const request: AddConcentratorRequest = {
      deviceType: Number(deviceType.id),
      model: asTrimmedString(input.model),
      serialNumber: asTrimmedString(input.serialNumber),
      firmwareVersion: asTrimmedString(input.firmwareVersion),
      status: NEW_DEVICE_STATUS,
      // Blank for non-DME-Admins: ConcentratorsService falls back to the user's own service center.
      serviceCenterCode: asTrimmedString(serviceCenter?.scCode),
    };

    const approved = await this.confirmService.confirm(
      {
        title: 'Approve new device?',
        message: 'An AI assistant wants to add this device to inventory.',
        details: [
          { label: 'Device type', value: [deviceType.type, deviceType.subType].filter(Boolean).join(' / ') },
          { label: 'Model', value: request.model },
          { label: 'Serial number', value: request.serialNumber },
          { label: 'Firmware', value: request.firmwareVersion },
          { label: 'Status', value: request.status },
          ...(serviceCenter ? [{ label: 'Service center', value: serviceCenter.name }] : []),
        ],
      },
      client,
    );

    if (!approved) {
      return errorResult('The signed-in user declined to create this device. Do not retry unless they ask you to.');
    }

    try {
      const response: any = await firstValueFrom(this.concentratorsService.addConcentrator(request));
      this.toastService.success('Device created successfully!', `Serial Number: ${request.serialNumber}`, 3000);
      return textResult({
        success: true,
        message: asTrimmedString(response?.message) || 'Device created.',
        device: response?.data ?? { serialNumber: request.serialNumber, model: request.model, status: request.status },
      });
    } catch (error) {
      return errorResult(`Device was not created: ${extractErrorMessage(error)}`);
    }
  }

  /** Matches by id, type or sub-type; returns undefined when nothing or more than one type matches. */
  private resolveDeviceType(deviceTypes: DeviceTypeOption[], value: string): DeviceTypeOption | undefined {
    const wanted = value.toLowerCase();
    const byId = deviceTypes.find((t) => asTrimmedString(t.id).toLowerCase() === wanted);
    if (byId) {
      return byId;
    }
    const byName = deviceTypes.filter(
      (t) => asTrimmedString(t.subType).toLowerCase() === wanted || asTrimmedString(t.type).toLowerCase() === wanted,
    );
    return byName.length === 1 ? byName[0] : undefined;
  }
}
